import { Settings, Network } from 'lucide-react';
import useStore from '../lib/store';

export default function DeviceConfig() {
  const selectedDevice = useStore(state => state.selectedDevice);
  const updateDevice = useStore(state => state.updateDevice);
  const devices = useStore(state => state.devices);

  const device = devices.find(d => d.id === selectedDevice?.id);

  if (!device) {
    return (
      <div className="config-empty">
        <Settings className="config-empty-icon" size={48} />
        <p>Select a device to configure its settings</p>
      </div>
    );
  }

  const handleChange = (field, value) => {
    updateDevice(device.id, { [field]: value });
  };

  const handleInterfaceChange = (portIndex, field, value) => {
    const newInterfaces = [...device.interfaces];
    newInterfaces[portIndex] = {
      ...newInterfaces[portIndex],
      [field]: value,
    };
    updateDevice(device.id, { interfaces: newInterfaces });
  };

  const handleTogglePower = () => {
    updateDevice(device.id, { powered: !device.powered });
  };

  const isRouter = device.type === 'router';

  return (
    <div className="config-container">
      <div className="config-group">
        <label className="config-label">Device Name</label>
        <input
          type="text"
          className="config-input"
          value={device.name}
          onChange={(e) => handleChange('name', e.target.value)}
        />
      </div>

      <div className="config-group">
        <label className="config-label">
          <Network size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
          Network Tag
        </label>
        <input
          type="text"
          className="config-input"
          value={device.networkTag || ''}
          onChange={(e) => handleChange('networkTag', e.target.value)}
          placeholder="e.g. Office Network, Admin Network"
        />
        <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem', display: 'block', marginTop: '0.25rem' }}>
          Group devices by network for better organization
        </small>
      </div>

      {!isRouter && (
        <>
          <div className="config-group">
            <label className="config-label">IP Address</label>
            <input
              type="text"
              className="config-input"
              value={device.ip}
              onChange={(e) => handleChange('ip', e.target.value)}
              placeholder="192.168.1.10"
            />
          </div>

          <div className="config-group">
            <label className="config-label">Subnet Mask</label>
            <input
              type="text"
              className="config-input"
              value={device.subnet}
              onChange={(e) => handleChange('subnet', e.target.value)}
              placeholder="255.255.255.0"
            />
          </div>

          <div className="config-group">
            <label className="config-label">Default Gateway</label>
            <input
              type="text"
              className="config-input"
              value={device.gateway}
              onChange={(e) => handleChange('gateway', e.target.value)}
              placeholder="192.168.1.1"
            />
          </div>

          <div className="config-group">
            <label className="config-label">DNS Server</label>
            <input
              type="text"
              className="config-input"
              value={device.dns}
              onChange={(e) => handleChange('dns', e.target.value)}
              placeholder="8.8.8.8"
            />
          </div>
        </>
      )}

      {isRouter && (
        <div className="config-group">
          <label className="config-label">Router Interfaces</label>
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
            Each port needs its own IP address when routing between networks
          </p>
          
          <div className="router-interfaces">
            {device.interfaces && device.interfaces.map((iface, index) => (
              <div key={index} className="interface-config">
                <div className="interface-header">
                  <strong>Port {index + 1}</strong>
                </div>
                
                <div className="interface-fields">
                  <div className="config-group-inline">
                    <label className="config-label-small">IP Address</label>
                    <input
                      type="text"
                      className="config-input-small"
                      value={iface.ip || ''}
                      onChange={(e) => handleInterfaceChange(index, 'ip', e.target.value)}
                      placeholder="192.168.1.1"
                    />
                  </div>
                  
                  <div className="config-group-inline">
                    <label className="config-label-small">Subnet Mask</label>
                    <input
                      type="text"
                      className="config-input-small"
                      value={iface.subnet || '255.255.255.0'}
                      onChange={(e) => handleInterfaceChange(index, 'subnet', e.target.value)}
                      placeholder="255.255.255.0"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="config-group">
        <label className="config-label">MAC Address</label>
        <input
          type="text"
          className="config-input"
          value={device.mac}
          disabled
        />
      </div>

      <div className="config-group">
        <label className="config-label">Device Type</label>
        <input
          type="text"
          className="config-input"
          value={device.type.toUpperCase()}
          disabled
        />
      </div>

      <div className="config-group">
        <label className="config-label">Power Status</label>
        <div className="toggle-container">
          <div
            className={`toggle ${device.powered ? 'active' : ''}`}
            onClick={handleTogglePower}
          >
            <div className="toggle-slider" />
          </div>
          <span className="toggle-label">
            {device.powered ? 'Powered On' : 'Powered Off'}
          </span>
        </div>
      </div>
    </div>
  );
}
